import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import ReactDOM from 'react-dom';
import ReCAPTCHA from 'react-google-recaptcha';

import styles from './ConnectModal.module.css';
import {isLocalDevelopment, PRISMAX_BACKEND_URL} from '../../configs/config';
import {
    APPLE_CLIENT_ID,
    APPLE_REDIRECT_URI,
    GOOGLE_CLIENT_ID,
    RECAPTCHA_V2_SITE_KEY
} from '../../configs/authPublicConfig';
import {useRecaptcha} from '../../utils/useRecaptcha';
import {SwapMainAppContext} from '../../context/SwapMainAppContext';
import {getLinkedWalletChainFromUserData} from '../../utils/walletChain';
import {getAccountRoleFromUserData, getQaRoleFromUserData} from '../../utils/userRoles';
import TooltipBottom from '../Tooltip/TooltipBottom';

import emailIcon from '../../static_files/connect-email.svg';
import phantomIcon from '../../static_files/Phantom Wallet.svg';
import metamaskIcon from '../../static_files/MetaMask-icon.svg';
import okxIcon from '../../static_files/OKX Wallet.png';
import walletConnectIcon from '../../static_files/walletconnect-icon.svg';
import baseIcon from '../../static_files/Base_square_blue.svg';
import coinbaseIcon from '../../static_files/Coinbase-icon.svg';
import robotThinkImg from '../../static_files/robotthink.svg';
import closeButtonImg from '../../static_files/closeButton.svg';
import closeButtonHoverImg from '../../static_files/closeButtonHover.svg';
import disconnectOrangeCamel from '../../static_files/disconnect-orange-camel.svg';
import disconnectOrangeTopaz from '../../static_files/disconnect-orange-topaz.svg';

const CODE_LENGTH = 6;

const loadExternalScript = (src, id) => new Promise((resolve, reject) => {
    if (typeof document === 'undefined') {
        reject(new Error('Browser environment is required.'));
        return;
    }

    const existingScript = document.getElementById(id);
    if (existingScript) {
        if (existingScript.dataset.loaded === 'true') {
            resolve();
            return;
        }
        existingScript.addEventListener('load', resolve, {once: true});
        existingScript.addEventListener('error', reject, {once: true});
        return;
    }

    const script = document.createElement('script');
    script.id = id;
    script.src = src;
    script.async = true;
    script.defer = true;
    script.onload = () => {
        script.dataset.loaded = 'true';
        resolve();
    };
    script.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(script);
});

const GoogleMark = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
);

const AppleMark = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
    </svg>
);

const ConnectModal = ({
                          isOpen,
                          onClose,
                          selectedChain,
                          chainOptions = [],
                          onChainSelect,
                          onWalletConnect,
                          userEmail = '',
                          connectedWalletAddress = '',
                          connectedWalletType = '',
                          onEmailDisconnect,
                          onWalletDisconnect,
                          onWalletUnlink,
                          showNotification,
                          openWalletListOnOpen = false,
                          forceWalletConnectView = false,
                          walletDetections = {}
                      }) => {
    const {executeRecaptchaAction, isRecaptchaReady} = useRecaptcha();
    const {
        setGatewayToken,
        setUserEmail,
        setUserClass,
        setUserRole,
        setQaRole,
        setUserId,
        setWalletAddress,
        setLinkedWalletChain,
        setSelectedChain
    } = useContext(SwapMainAppContext);

    const recaptchaV2SiteKey = RECAPTCHA_V2_SITE_KEY;
    const [view, setView] = useState('connect');
    const [email, setEmail] = useState('');
    const [code, setCode] = useState('');
    const [isWalletListOpen, setIsWalletListOpen] = useState(false);
    const [secondsRemaining, setSecondsRemaining] = useState(0);
    const [isBusy, setIsBusy] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [infoMessage, setInfoMessage] = useState('');
    const [showRecaptchaModal, setShowRecaptchaModal] = useState(false);
    const [pendingRecaptchaAction, setPendingRecaptchaAction] = useState(null);
    const [recaptchaCloseHover, setRecaptchaCloseHover] = useState(false);
    const [isHoveredUnlinkWallet, setIsHoveredUnlinkWallet] = useState(false);
    const otpRefs = useRef([]);
    const countdownRef = useRef(null);
    const googleTokenClientRef = useRef(null);

    const walletRows = useMemo(() => ([
        {key: 'phantom', label: 'Phantom', icon: phantomIcon, detected: walletDetections.phantom},
        {key: 'metamask', label: 'MetaMask', icon: metamaskIcon, detected: walletDetections.metamask},
        {key: 'okx', label: 'OKX Wallet', icon: okxIcon, detected: walletDetections.okx},
        {key: 'coinbase', label: 'Coinbase Wallet', icon: coinbaseIcon, detected: walletDetections.coinbase},
        ...(selectedChain === 'solana'
            ? [{key: 'walletconnect', label: 'WalletConnect', icon: walletConnectIcon, detected: false}]
            : [])
    ]), [selectedChain, walletDetections]);

    const isWalletConnected = Boolean(connectedWalletAddress);
    const isConnected = Boolean(userEmail || connectedWalletAddress);
    const normalizedConnectedWalletType = (connectedWalletType || '').toLowerCase();
    const resetModal = useCallback(() => {
        setView(isConnected && !forceWalletConnectView ? 'connected' : 'connect');
        setEmail('');
        setCode('');
        setIsWalletListOpen(Boolean(openWalletListOnOpen));
        setSecondsRemaining(0);
        setIsBusy(false);
        setErrorMessage('');
        setInfoMessage('');
        setShowRecaptchaModal(false);
        setPendingRecaptchaAction(null);
    }, [forceWalletConnectView, isConnected, openWalletListOnOpen]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        resetModal();
    }, [isOpen, resetModal]);

    useEffect(() => {
        if (!isOpen || secondsRemaining <= 0) {
            if (countdownRef.current) {
                clearTimeout(countdownRef.current);
                countdownRef.current = null;
            }
            return;
        }

        countdownRef.current = setTimeout(() => {
            setSecondsRemaining((current) => Math.max(0, current - 1));
        }, 1000);

        return () => {
            if (countdownRef.current) {
                clearTimeout(countdownRef.current);
                countdownRef.current = null;
            }
        };
    }, [isOpen, secondsRemaining]);

    useEffect(() => {
        if (view === 'otp') {
            otpRefs.current[0]?.focus();
        }
    }, [view]);

    const getReferralCode = useCallback(() => {
        const params = new URLSearchParams(window.location.search);
        return params.get('ref') || localStorage.getItem('referralCode') || null;
    }, []);

    const shortenAddress = useCallback((address, startLength = 6, endLength = 4) => {
        if (!address) {
            return '';
        }
        if (address.length <= startLength + endLength) {
            return address;
        }
        return `${address.slice(0, startLength)}...${address.slice(-endLength)}`;
    }, []);

    const getChainName = useCallback((chain) => {
        const chainNames = {
            solana: 'Solana',
            ethereum: 'Ethereum',
            base: 'Base',
            monad: 'Monad'
        };
        return chainNames[chain] || chain?.charAt(0)?.toUpperCase() + chain?.slice(1) || 'Wallet';
    }, []);

    const walletMeta = useMemo(() => {
        const byType = {
            phantom: {label: 'Phantom Wallet', icon: phantomIcon, avatarClass: styles.walletAvatarPhantom},
            metamask: {label: 'MetaMask', icon: metamaskIcon, avatarClass: styles.walletAvatarMetamask},
            okx: {label: 'OKX Wallet', icon: okxIcon, avatarClass: styles.walletAvatarOkx},
            coinbase: {label: 'Coinbase Wallet', icon: coinbaseIcon, avatarClass: styles.walletAvatarCoinbase},
            walletconnect: {label: 'WalletConnect', icon: walletConnectIcon, avatarClass: styles.walletAvatarWalletConnect}
        };
        return byType[normalizedConnectedWalletType] || {
            label: 'Wallet',
            icon: baseIcon,
            avatarClass: styles.walletAvatarDefault
        };
    }, [normalizedConnectedWalletType]);

    const hasEmail = Boolean(userEmail);
    const primaryIsEmail = hasEmail;
    const connectedDisplay = primaryIsEmail ? userEmail : shortenAddress(connectedWalletAddress);
    const connectedVia = primaryIsEmail ? 'Email' : `${walletMeta.label} · ${getChainName(selectedChain)}`;
    const connectedInitials = (userEmail || 'PX')
        .split('@')[0]
        .replace(/[^a-zA-Z0-9]/g, '')
        .slice(0, 2)
        .toUpperCase() || 'PX';
    const walletDisplay = shortenAddress(connectedWalletAddress);
    const walletVia = `${walletMeta.label} · ${getChainName(selectedChain)}`;
    const disconnectLabel = 'Disconnect';

    const applyUserSession = useCallback((userData) => {
        const token = userData?.hash_code;
        const normalizedEmail = (userData?.email || email).trim().toLowerCase();

        if (!token || !normalizedEmail) {
            throw new Error('Login response is missing session information.');
        }

        localStorage.setItem('gatewayToken', token);
        localStorage.setItem('emailLogin', 'true');
        localStorage.setItem('userEmail', normalizedEmail);
        setGatewayToken(token);
        setUserEmail(normalizedEmail);

        if (userData?.userid) {
            localStorage.setItem('userId', userData.userid);
            setUserId(Number(userData.userid));
        }
        if (userData?.user_class) {
            localStorage.setItem('userClass', userData.user_class);
            setUserClass(userData.user_class);
        }
        const nextUserRole = getAccountRoleFromUserData(userData);
        const nextQaRole = getQaRoleFromUserData(userData);
        setUserRole(nextUserRole);
        setQaRole(nextQaRole);
        if (nextUserRole) localStorage.setItem('userRole', nextUserRole);
        else localStorage.removeItem('userRole');
        if (nextQaRole) localStorage.setItem('qaRole', nextQaRole);
        else localStorage.removeItem('qaRole');
        if (userData?.total_points) {
            localStorage.setItem('totalPoints', userData.total_points);
        }
        if (userData?.linked_wallet_address) {
            const linkedWalletChain = getLinkedWalletChainFromUserData(userData);
            localStorage.setItem('walletAddress', userData.linked_wallet_address);
            setWalletAddress(userData.linked_wallet_address);
            if (linkedWalletChain) {
                localStorage.setItem('linkedWalletChain', linkedWalletChain);
                localStorage.setItem('selectedChain', linkedWalletChain);
                setLinkedWalletChain?.(linkedWalletChain);
                setSelectedChain?.(linkedWalletChain);
            }
        }

        onClose();
    }, [email, onClose, setGatewayToken, setLinkedWalletChain, setQaRole, setSelectedChain, setUserClass, setUserEmail, setUserId, setUserRole, setWalletAddress]);

    const completeLogin = useCallback(async (sessionData) => {
        const token = sessionData?.hash_code;
        if (!token) {
            throw new Error('Login response did not include a token.');
        }

        const response = await fetch(`${PRISMAX_BACKEND_URL}/verify_token`, {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({token})
        });
        const result = await response.json().catch(() => ({}));

        if (!response.ok || !result.success) {
            throw new Error(result?.msg || 'Unable to verify your session.');
        }

        applyUserSession(result.data || sessionData);
    }, [applyUserSession]);

    const requireRecaptchaV2 = useCallback((action) => {
        if (isLocalDevelopment && !recaptchaV2SiteKey) {
            action('test');
            return;
        }

        if (!recaptchaV2SiteKey) {
            setErrorMessage('Security verification is unavailable. Please contact support.');
            return;
        }

        setPendingRecaptchaAction(() => action);
        setShowRecaptchaModal(true);
    }, [recaptchaV2SiteKey]);

    const sendVerificationCode = useCallback(async (recaptchaV2Token) => {
        const normalizedEmail = email.trim().toLowerCase();

        if (!normalizedEmail) {
            setErrorMessage('Please enter your email address.');
            return;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
            setErrorMessage('Please enter a valid email address.');
            return;
        }
        if (!isRecaptchaReady) {
            setInfoMessage('Security verification is initializing. Please wait a moment.');
            return;
        }

        setIsBusy(true);
        setErrorMessage('');
        setInfoMessage('');

        try {
            const recaptchaToken = await executeRecaptchaAction('send_code');
            const response = await fetch(`${PRISMAX_BACKEND_URL}/auth/send-verification-code`, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({
                    email: normalizedEmail,
                    recaptchaToken,
                    recaptchaV2Token
                })
            });
            const result = await response.json().catch(() => ({}));

            if (!response.ok || !result.success) {
                throw new Error(result?.msg || 'Failed to send verification code.');
            }

            setEmail(normalizedEmail);
            setCode('');
            setSecondsRemaining(30);
            setView('otp');
            setInfoMessage('Verification code sent.');
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'Failed to send verification code.');
        } finally {
            setIsBusy(false);
        }
    }, [email, executeRecaptchaAction, isRecaptchaReady]);

    const handleEmailSubmit = useCallback((event) => {
        event.preventDefault();
        requireRecaptchaV2(sendVerificationCode);
    }, [requireRecaptchaV2, sendVerificationCode]);

    const verifyEmailCode = useCallback(async (nextCode) => {
        if (nextCode.length !== CODE_LENGTH || isBusy) {
            return;
        }
        if (!isRecaptchaReady) {
            setInfoMessage('Security verification is initializing. Please wait a moment.');
            return;
        }

        setIsBusy(true);
        setErrorMessage('');
        setInfoMessage('');

        try {
            const recaptchaToken = await executeRecaptchaAction('login');
            const response = await fetch(`${PRISMAX_BACKEND_URL}/auth/save`, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({
                    email,
                    code: nextCode,
                    type: '0',
                    recaptchaToken,
                    referrers_referral_code: getReferralCode()
                })
            });
            const result = await response.json().catch(() => ({}));

            if (!response.ok || !result.success) {
                throw new Error(result?.msg || 'Invalid verification code.');
            }

            await completeLogin(result.data);
        } catch (error) {
            setCode('');
            setErrorMessage(error instanceof Error ? error.message : 'Verification failed.');
            otpRefs.current[0]?.focus();
        } finally {
            setIsBusy(false);
        }
    }, [completeLogin, email, executeRecaptchaAction, getReferralCode, isBusy, isRecaptchaReady]);

    const handleCodeChange = useCallback((index, value) => {
        const digit = value.replace(/\D/g, '').slice(-1);
        const chars = code.padEnd(CODE_LENGTH, ' ').split('');
        chars[index] = digit || ' ';
        const nextCode = chars.join('').replace(/\s/g, '');
        setCode(nextCode);
        setErrorMessage('');

        if (digit && index < CODE_LENGTH - 1) {
            otpRefs.current[index + 1]?.focus();
        }
        if (nextCode.length === CODE_LENGTH) {
            verifyEmailCode(nextCode);
        }
    }, [code, verifyEmailCode]);

    const handleCodeKeyDown = useCallback((index, event) => {
        if (event.key === 'Backspace' && !code[index] && index > 0) {
            otpRefs.current[index - 1]?.focus();
        }
    }, [code]);

    const handleCodePaste = useCallback((event) => {
        const pastedCode = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, CODE_LENGTH);
        if (!pastedCode) {
            return;
        }
        event.preventDefault();
        setCode(pastedCode);
        if (pastedCode.length === CODE_LENGTH) {
            verifyEmailCode(pastedCode);
        }
    }, [verifyEmailCode]);

    const handleResend = useCallback(() => {
        if (secondsRemaining > 0 || isBusy) {
            return;
        }
        requireRecaptchaV2(sendVerificationCode);
    }, [isBusy, requireRecaptchaV2, secondsRemaining, sendVerificationCode]);

    const handleOAuthLogin = useCallback(async (provider, providerToken) => {
        if (!isRecaptchaReady) {
            setInfoMessage('Security verification is initializing. Please wait a moment.');
            return;
        }

        setIsBusy(true);
        setErrorMessage('');
        setInfoMessage('');

        try {
            const recaptchaToken = await executeRecaptchaAction('login');
            const response = await fetch(`${PRISMAX_BACKEND_URL}/auth/oauth-login`, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({
                    provider,
                    providerToken,
                    recaptchaToken,
                    referrers_referral_code: getReferralCode()
                })
            });
            const result = await response.json().catch(() => ({}));

            if (!response.ok || !result.success) {
                throw new Error(result?.msg || `${provider} login failed.`);
            }

            applyUserSession(result.data);
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : `${provider} login failed.`);
        } finally {
            setIsBusy(false);
        }
    }, [applyUserSession, executeRecaptchaAction, getReferralCode, isRecaptchaReady]);

    const handleGoogleLogin = useCallback(async () => {
        try {
            setErrorMessage('');
            await loadExternalScript('https://accounts.google.com/gsi/client', 'google-identity-services');
            if (!window.google?.accounts?.oauth2) {
                throw new Error('Google login is unavailable.');
            }

            googleTokenClientRef.current = window.google.accounts.oauth2.initTokenClient({
                client_id: GOOGLE_CLIENT_ID,
                scope: 'openid email profile',
                callback: (response) => {
                    if (response?.access_token) {
                        handleOAuthLogin('google', response.access_token);
                    } else {
                        setErrorMessage('Google login did not return a token.');
                    }
                },
                error_callback: () => setErrorMessage('Google login was cancelled.')
            });
            googleTokenClientRef.current.requestAccessToken({prompt: 'consent'});
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'Google login failed.');
        }
    }, [handleOAuthLogin]);

    const handleAppleLogin = useCallback(async () => {
        try {
            setErrorMessage('');
            await loadExternalScript(
                'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js',
                'appleid-auth'
            );
            if (!window.AppleID?.auth) {
                throw new Error('Apple login is unavailable.');
            }

            window.AppleID.auth.init({
                clientId: APPLE_CLIENT_ID,
                scope: 'name email',
                redirectURI: APPLE_REDIRECT_URI,
                usePopup: true
            });

            const response = await window.AppleID.auth.signIn();
            const idToken = response?.authorization?.id_token;
            if (!idToken) {
                throw new Error('Apple login did not return a token.');
            }
            await handleOAuthLogin('apple', idToken);
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'Apple login failed.');
        }
    }, [handleOAuthLogin]);

    const handleWalletClick = useCallback(async (walletKey) => {
        if (!onWalletConnect) {
            return;
        }
        setErrorMessage('');
        setIsBusy(true);
        try {
            await onWalletConnect(walletKey);
            onClose();
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'Wallet connection failed.');
        } finally {
            setIsBusy(false);
        }
    }, [onClose, onWalletConnect]);

    const handleConnectedDisconnect = useCallback(async () => {
        setIsBusy(true);
        setErrorMessage('');
        setInfoMessage('');
        try {
            if (userEmail && onEmailDisconnect) {
                await onEmailDisconnect();
            } else if (isWalletConnected && onWalletDisconnect) {
                await onWalletDisconnect();
            }
            onClose();
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'Disconnect failed.');
        } finally {
            setIsBusy(false);
        }
    }, [isWalletConnected, onClose, onEmailDisconnect, onWalletDisconnect, userEmail]);

    const handleWalletUnlink = useCallback(async () => {
        if (!onWalletUnlink) {
            return;
        }
        setIsBusy(true);
        setErrorMessage('');
        setInfoMessage('');
        try {
            await onWalletUnlink();
            showNotification?.('success', 'Your wallet has been successfully unlinked from your email.');
        } catch (error) {
            showNotification?.(
                'error',
                error instanceof Error ? error.message : 'Failed to unlink your wallet. Please try again.'
            );
        } finally {
            setIsBusy(false);
        }
    }, [onWalletUnlink, showNotification]);

    const handleCopyWalletAddress = useCallback(async () => {
        if (!connectedWalletAddress) {
            return;
        }
        try {
            await navigator.clipboard.writeText(connectedWalletAddress);
            setInfoMessage('Wallet address copied.');
        } catch {
            setErrorMessage('Unable to copy wallet address.');
        }
    }, [connectedWalletAddress]);

    const handleClose = useCallback(() => {
        resetModal();
        onClose();
    }, [onClose, resetModal]);

    const handleRecaptchaChange = useCallback(async (token) => {
        if (!token || !pendingRecaptchaAction) {
            setErrorMessage('reCAPTCHA verification failed. Please try again.');
            return;
        }

        const action = pendingRecaptchaAction;
        setShowRecaptchaModal(false);
        setPendingRecaptchaAction(null);
        await action(token);
    }, [pendingRecaptchaAction]);

    if (!isOpen) {
        return null;
    }

    const codeChars = code.padEnd(CODE_LENGTH, ' ').split('');
    const shouldShowConnectedWalletSection = hasEmail && !isWalletConnected;
    const renderWalletConnectSection = () => (
        <>
            <button
                type="button"
                className={styles.walletToggle}
                onClick={() => setIsWalletListOpen((current) => !current)}
                disabled={isBusy}
            >
                <span className={styles.walletToggleLeft}>
                    <span className={styles.walletToggleIcon}>
                        <img src={emailIcon} alt="" aria-hidden="true"/>
                    </span>
                    <span className={styles.walletLabel}>Connect Wallet</span>
                </span>
                <span className={`${styles.walletChevron} ${isWalletListOpen ? styles.walletChevronOpen : ''}`}>
                    <svg width="10" height="6" viewBox="0 0 10 6" fill="none" aria-hidden="true">
                        <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </span>
            </button>

            {isWalletListOpen && (
                <div className={styles.walletList}>
                    <div className={styles.sectionLabel}>Network</div>
                    <div className={styles.chainSelector}>
                        {chainOptions.map((chain) => (
                            <button
                                type="button"
                                key={chain.key}
                                className={`${styles.chainButton} ${selectedChain === chain.key ? styles.chainButtonActive : ''}`}
                                onClick={() => onChainSelect?.(chain.key)}
                                disabled={isBusy}
                                aria-pressed={selectedChain === chain.key}
                            >
                                <span className={styles.chainIcon}>
                                    <img src={chain.icon} alt="" aria-hidden="true"/>
                                </span>
                                {chain.label}
                            </button>
                        ))}
                    </div>
                    <div className={styles.walletSectionDivider}/>
                    <div className={styles.sectionLabel}>Wallet</div>
                    {walletRows.map((wallet) => (
                        <button
                            type="button"
                            key={wallet.key}
                            className={styles.walletRow}
                            onClick={() => handleWalletClick(wallet.key)}
                            disabled={isBusy}
                        >
                            <span className={styles.walletIcon}>
                                <img src={wallet.icon} alt="" aria-hidden="true"/>
                            </span>
                            <span className={styles.walletName}>{wallet.label}</span>
                            {wallet.detected && <span className={styles.walletTag}>detected</span>}
                        </button>
                    ))}
                </div>
            )}
        </>
    );

    return ReactDOM.createPortal(
        <div className={styles.overlay} onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
                handleClose();
            }
        }}>
            <div className={`${styles.modal} ${view === 'connected' ? styles.connectedModal : ''}`} role="dialog" aria-modal="true" aria-labelledby="connect-modal-title">
                <div className={styles.header}>
                    <span id="connect-modal-title" className={styles.title}>Connect to PrismaX</span>
                    <button className={styles.closeButton} onClick={handleClose} aria-label="Close connect modal">
                        <svg width="9" height="9" viewBox="0 0 9 9" fill="none" aria-hidden="true">
                            <path d="M1 1l7 7M8 1L1 8" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
                        </svg>
                    </button>
                </div>
                <div className={styles.divider}/>

                {view === 'connect' && (
                    <div className={styles.body}>
                        <form className={styles.emailField} onSubmit={handleEmailSubmit}>
                            <input
                                className={styles.emailInput}
                                type="email"
                                value={email}
                                onChange={(event) => {
                                    setEmail(event.target.value);
                                    setErrorMessage('');
                                }}
                                placeholder="Your email address"
                                autoComplete="email"
                                disabled={isBusy}
                            />
                            <button className={styles.primaryButton} disabled={isBusy}>
                                {isBusy ? 'Connecting' : 'Connect'}
                            </button>
                        </form>

                        <div className={styles.socialRow}>
                            <button className={styles.socialButton} onClick={handleGoogleLogin} disabled={isBusy}>
                                <GoogleMark/>
                                Google
                            </button>
                            <button className={styles.socialButton} onClick={handleAppleLogin} disabled={isBusy}>
                                <AppleMark/>
                                Apple
                            </button>
                        </div>

                        <div className={styles.orRow}>
                            <div className={styles.orLine}/>
                            <span className={styles.orText}>or</span>
                            <div className={styles.orLine}/>
                        </div>

                        {renderWalletConnectSection()}
                    </div>
                )}

                {view === 'otp' && (
                    <div className={`${styles.body} ${styles.otpBody}`}>
                        <p className={styles.otpTitle}>Check your email</p>
                        <p className={styles.otpSub}>We sent a code to <span>{email}</span></p>
                        <div className={styles.otpInputs} onPaste={handleCodePaste}>
                            {Array.from({length: CODE_LENGTH}).map((_, index) => (
                                <input
                                    key={index}
                                    ref={(node) => {
                                        otpRefs.current[index] = node;
                                    }}
                                    className={`${styles.otpCell} ${codeChars[index].trim() ? styles.otpCellFilled : ''}`}
                                    type="text"
                                    inputMode="numeric"
                                    maxLength="1"
                                    value={codeChars[index].trim()}
                                    onChange={(event) => handleCodeChange(index, event.target.value)}
                                    onKeyDown={(event) => handleCodeKeyDown(index, event)}
                                    disabled={isBusy}
                                    aria-label={`Verification code digit ${index + 1}`}
                                />
                            ))}
                        </div>
                        <div className={styles.otpActions}>
                            <button
                                className={`${styles.linkButton} ${secondsRemaining === 0 ? styles.linkButtonReady : ''}`}
                                onClick={handleResend}
                                disabled={secondsRemaining > 0 || isBusy}
                            >
                                Resend code
                            </button>
                            <button
                                className={styles.changeButton}
                                onClick={() => {
                                    setView('connect');
                                    setCode('');
                                    setInfoMessage('');
                                    setErrorMessage('');
                                }}
                                disabled={isBusy}
                            >
                                Change email
                            </button>
                        </div>
                        <p className={styles.timerText}>
                            {secondsRemaining > 0 ? `Please wait ${secondsRemaining} seconds before sending another email` : ''}
                        </p>
                    </div>
                )}

                {view === 'connected' && (
                    <div className={`${styles.body} ${styles.connectedBody}`}>
                        <div className={styles.statusRow}>
                            <span className={styles.statusDot}/>
                            <span className={styles.statusText}>Connected</span>
                        </div>
                        <div className={styles.accountCard}>
                            {primaryIsEmail ? (
                                <div className={styles.accountAvatar}>{connectedInitials}</div>
                            ) : (
                                <div className={`${styles.walletAccountAvatar} ${walletMeta.avatarClass}`}>
                                    <img src={walletMeta.icon} alt="" aria-hidden="true"/>
                                </div>
                            )}
                            <div className={styles.accountInfo}>
                                <div className={styles.accountName}>{connectedDisplay}</div>
                                <div className={styles.accountVia}>
                                    {primaryIsEmail ? (
                                        <span className={styles.viaIcon} aria-hidden="true">
                                            <svg width="18" height="14" viewBox="0 0 18 14" fill="none">
                                                <path d="M2.25 1.75h13.5c.62 0 1.13.5 1.13 1.13v8.24c0 .63-.51 1.13-1.13 1.13H2.25c-.62 0-1.13-.5-1.13-1.13V2.88c0-.63.51-1.13 1.13-1.13Z" stroke="currentColor" strokeWidth="1.4"/>
                                                <path d="m2.25 3 6.75 4.5L15.75 3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                        </span>
                                    ) : (
                                        <span className={styles.viaIcon} aria-hidden="true">
                                            <svg width="18" height="14" viewBox="0 0 18 14" fill="none">
                                                <rect x="1.2" y="2.2" width="15.6" height="9.6" rx="1.8" stroke="currentColor" strokeWidth="1.4"/>
                                                <path d="M12.6 5h2.4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
                                            </svg>
                                        </span>
                                    )}
                                    {primaryIsEmail ? `via ${connectedVia}` : connectedVia}
                                </div>
                            </div>
                            {!primaryIsEmail && isWalletConnected && connectedWalletAddress && (
                                <button
                                    type="button"
                                    className={styles.copyButton}
                                    onClick={handleCopyWalletAddress}
                                    aria-label="Copy wallet address"
                                >
                                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
                                        <rect x="6" y="6" width="9" height="9" rx="1.8" stroke="currentColor" strokeWidth="1.4"/>
                                        <path d="M3 11.25V4.8C3 3.8 3.8 3 4.8 3h6.45" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
                                    </svg>
                                </button>
                            )}
                        </div>
                        {hasEmail && isWalletConnected && connectedWalletAddress && (
                            <div className={styles.linkedWalletCard}>
                                <div className={`${styles.linkedWalletAvatar} ${walletMeta.avatarClass}`}>
                                    <img src={walletMeta.icon} alt="" aria-hidden="true"/>
                                </div>
                                <div className={styles.accountInfo}>
                                    <div className={styles.linkedWalletLabel}>Linked wallet</div>
                                    <div className={styles.accountName}>{walletDisplay}</div>
                                    <div className={styles.accountVia}>
                                        <span className={styles.viaIcon} aria-hidden="true">
                                            <svg width="18" height="14" viewBox="0 0 18 14" fill="none">
                                                <rect x="1.2" y="2.2" width="15.6" height="9.6" rx="1.8" stroke="currentColor" strokeWidth="1.4"/>
                                                <path d="M12.6 5h2.4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
                                            </svg>
                                        </span>
                                        {walletVia}
                                    </div>
                                </div>
                                <TooltipBottom
                                    text="Unlink Wallet"
                                    enableTooltipHoverEffect
                                    onHoverChange={setIsHoveredUnlinkWallet}
                                >
                                    <button
                                        type="button"
                                        className={styles.linkedWalletUnlinkButton}
                                        onClick={handleWalletUnlink}
                                        disabled={isBusy || !onWalletUnlink}
                                        aria-label="Unlink wallet"
                                    >
                                        <img src={isHoveredUnlinkWallet ? disconnectOrangeTopaz : disconnectOrangeCamel} alt=""/>
                                    </button>
                                </TooltipBottom>
                            </div>
                        )}
                        {shouldShowConnectedWalletSection && renderWalletConnectSection()}
                        <button
                            type="button"
                            className={styles.disconnectButton}
                            onClick={handleConnectedDisconnect}
                            disabled={isBusy}
                        >
                            <span className={styles.disconnectIcon} aria-hidden="true">
                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                    <path d="M9 2h3v10H9M6 4L2 7l4 3M2 7h8" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </span>
                            {disconnectLabel}
                        </button>
                    </div>
                )}

                {(errorMessage || infoMessage) && (
                    <div className={errorMessage ? styles.errorMessage : styles.infoMessage}>
                        {errorMessage || infoMessage}
                    </div>
                )}

            </div>

            {showRecaptchaModal && (
                <div className={styles.recaptchaOverlay} onMouseDown={(event) => {
                    if (event.target === event.currentTarget) {
                        setShowRecaptchaModal(false);
                        setPendingRecaptchaAction(null);
                    }
                }}>
                    <div className={styles.recaptchaModal}>
                        <div className={styles.recaptchaTopRow}>
                            <img
                                onMouseEnter={() => setRecaptchaCloseHover(true)}
                                onMouseLeave={() => setRecaptchaCloseHover(false)}
                                src={recaptchaCloseHover ? closeButtonHoverImg : closeButtonImg}
                                className={styles.recaptchaCloseButton}
                                onClick={() => {
                                    setShowRecaptchaModal(false);
                                    setPendingRecaptchaAction(null);
                                    setRecaptchaCloseHover(false);
                                }}
                                alt="Close"
                            />
                        </div>
                        <img src={robotThinkImg} className={styles.recaptchaIllustration} alt="Security check"/>
                        <p className={styles.recaptchaTitle}>Hey! Are you human?</p>
                        <p className={styles.recaptchaText}>Please confirm you are not a robot</p>
                        <div className={styles.recaptchaWidget}>
                            <ReCAPTCHA
                                sitekey={recaptchaV2SiteKey}
                                useRecaptchaNet
                                onChange={handleRecaptchaChange}
                                onExpired={() => setErrorMessage('reCAPTCHA verification expired. Please try again.')}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>,
        document.body
    );
};

export default ConnectModal;
